import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { renderDefaultValueRenderer } from '@next-adapter/templates/renderers/generators/form/form-shared/generate-default-value.js'
import { defaultFallbackValue } from './default-fallback-value.js'
import { generateNestedListDefaultValue } from './generate-nested-list-default-value.js'
import { hasInitialValueExpression } from './has-initial-value-expression.js'
import { selectDefaultValues } from './select-default-values.js'
import { selectReadValuesExpression } from './select-read-values-expression.js'

export function generateDefaultValue(
  f: SchemaField,
  fieldMap?: Map<string, SchemaField>,
  dataSource = 'initialData',
  options: { slugFallbackValue?: string } = {}
): string {
  if (f.name === 'slug' && !f.hidden && options.slugFallbackValue) {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? '${options.slugFallbackValue}'`
  }

  if (
    f.defaultValueFrom &&
    f.defaultValueFrom.trim() !== f.name &&
    fieldMap?.has(f.defaultValueFrom.trim())
  ) {
    const sourceName = f.defaultValueFrom.trim()
    if (f.type === 'select') {
      const targetValuesExpression = selectReadValuesExpression(f.name, dataSource)
      const sourceValuesExpression = selectReadValuesExpression(sourceName, dataSource)
      if (f.multiple) {
        const fallback = JSON.stringify(selectDefaultValues(f))
        return renderDefaultValueRenderer({
          kind: 'renderDefaultValueContentTemplate',
          props: {
            fName: f.name,
            targetValuesExpression: targetValuesExpression,
            sourceValuesExpression: sourceValuesExpression,
            fallback: fallback
          }
        })
      }

      const fallback =
        f.default !== undefined && f.default !== null ? JSON.stringify(f.default) : "''"
      return `      ${f.name}: ${targetValuesExpression}[0] ?? ${sourceValuesExpression}[0] ?? ${fallback}`
    }

    return renderDefaultValueRenderer({
      kind: 'renderDefaultValueContentDetailsTemplate',
      props: {
        fName: f.name,
        hasInitialValueExpression: hasInitialValueExpression(f.name, dataSource),
        dataSource: dataSource,
        hasInitialValueExpressionText: hasInitialValueExpression(sourceName, dataSource),
        sourceName: sourceName,
        defaultFallbackValue: defaultFallbackValue(f)
      }
    })
  }
  if (f.type === 'list' && f.fields && f.fields.length > 0) {
    return generateNestedListDefaultValue(f, dataSource)
  }
  if (f.type === 'list' || f.type === 'gallery')
    return `      ${f.name}: ${dataSource}?.${f.name} ?? []`
  if (f.type === 'boolean') {
    const def = f.default === true ? 'true' : 'false'
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  if (f.type === 'number' || f.type === 'decimal') {
    const def = f.required ? '0' : 'undefined'
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  if (f.type === 'uuid' || f.type === 'serial') {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ''`
  }
  if (f.type === 'relationship') {
    if (f.multiple) return `      ${f.name}: ${dataSource}?.${f.name} ?? []`
    return renderDefaultValueRenderer({
      kind: 'renderDefaultValueContentDetailsBranchTemplate',
      props: {
        fName: f.name,
        dataSource: dataSource
      }
    })
  }
  if (f.type === 'date' || f.type === 'timestamp') {
    return `      ${f.name}: ${dataSource}?.${f.name} ?? undefined`
  }
  if (f.type === 'select') {
    const selectedValuesExpression = selectReadValuesExpression(f.name, dataSource)
    if (f.multiple) {
      const fallback = JSON.stringify(selectDefaultValues(f))
      if (f.creatable) {
        return `      ${f.name}: ${selectedValuesExpression}.length > 0 ? ${selectedValuesExpression} : ${fallback}`
      }
      const validValuesExpression = `([${(f.options || []).map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
      return renderDefaultValueRenderer({
        kind: 'renderDefaultValueContentDetailsBranchBodyTemplate',
        props: {
          fName: f.name,
          selectedValuesExpression: selectedValuesExpression,
          validValuesExpression: validValuesExpression,
          fallback: fallback
        }
      })
    }
    const fallback =
      f.default !== undefined && f.default !== null ? JSON.stringify(f.default) : "''"
    if (f.creatable) {
      return `      ${f.name}: ${selectedValuesExpression}[0] ?? ${fallback}`
    }
    const validValuesExpression = `([${(f.options || []).map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
    return `      ${f.name}: ${selectedValuesExpression}[0] && ${validValuesExpression}.includes(${selectedValuesExpression}[0]) ? ${selectedValuesExpression}[0] : ${fallback}`
  }
  if (f.default !== undefined && f.default !== null) {
    const def = typeof f.default === 'string' ? `'${f.default}'` : f.default
    return `      ${f.name}: ${dataSource}?.${f.name} ?? ${def}`
  }
  return `      ${f.name}: ${dataSource}?.${f.name} ?? ''`
}
